import {
  type File as FileModel,
  removeNestedFieldsExcept,
  SIGNATURE_FILES_MAX_IDS,
  signatureFiles,
  type SignatureFilesQueryVariables,
} from '@rentguru/commons-utils';
import { useQuery } from '@tanstack/react-query';
import { query } from '@up2rent/graphql';
import chunk from 'lodash/chunk';
import isEmpty from 'lodash/isEmpty';

import { buildLiveFiles } from './liveFilesUtils';

export const LIVE_FILES_QUERY_KEY = 'live-files';

const NESTED_FILE_FIELDS = ['category', 'signaturePositions'];
const MAXIMUM_DEPTH_TO_KEEP = 1;
const signatureFilesQuery = removeNestedFieldsExcept(signatureFiles, MAXIMUM_DEPTH_TO_KEEP, NESTED_FILE_FIELDS);

const fetchLiveFiles = async (fileIds: string[]): Promise<Map<string, FileModel | null>> => {
  const fileIdBatches = chunk(fileIds, SIGNATURE_FILES_MAX_IDS);
  const outcomes = await Promise.allSettled(
    fileIdBatches.map((batch) =>
      query<FileModel[], SignatureFilesQueryVariables>(signatureFilesQuery, { input: { fileIds: batch } })
    )
  );
  return buildLiveFiles(fileIdBatches, outcomes);
};

export const useLiveFiles = (fileIds: string[], enabled: boolean) => {
  const { data, isLoading } = useQuery<Map<string, FileModel | null>>({
    queryKey: [LIVE_FILES_QUERY_KEY, fileIds],
    queryFn: () => fetchLiveFiles(fileIds),
    enabled: enabled && !isEmpty(fileIds),
    refetchOnWindowFocus: false,
  });

  return { liveFiles: data ?? null, loading: isLoading };
};
